import { useEffect, useRef } from 'react';
import { groupColor } from './mapColors';
import { computeExploredFaces } from './fogRegions';
import type { MapLocation, PlayerPosition } from '@/types';

interface MapState {
  zoom: number;
  pan: { x: number; y: number };
  selectedId: string | null;
  travelTarget: string | null;
  currentPlayerId: number | null;
}

interface MapData {
  locations: MapLocation[];
  positions: PlayerPosition[];
  explored: Set<string>;
}

interface RendererArgs {
  canvas: HTMLCanvasElement | null;
  mapImage: HTMLImageElement | null;
  state: MapState;
  data: MapData;
  avatars: Map<number, HTMLImageElement>;
  /** Logical (CSS) pixel viewport size — independent of devicePixelRatio. */
  viewportW: number;
  viewportH: number;
}

/**
 * Imperative canvas renderer for the world map. Returns a draw() function plus
 * a fog-of-war cache invalidator. State is held in refs so pan/zoom interactions
 * don't trigger React re-renders.
 */
export function useMapRenderer() {
  const fogCache = useRef<HTMLCanvasElement | null>(null);
  const fogCacheKey = useRef<string>('');
  const rafRef = useRef<number | null>(null);
  const pendingArgsRef = useRef<RendererArgs | null>(null);

  function buildFogCanvas(
    locations: MapLocation[],
    explored: Set<string>,
    mapW: number,
    mapH: number,
  ): HTMLCanvasElement {
    const NODE_R = 70;
    const CORRIDOR_W = 80;
    const HINT_R = 60;
    const FEATHER = 22;

    const canvas = document.createElement('canvas');
    canvas.width = mapW;
    canvas.height = mapH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // Fog layer over the whole map.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.78)';
    ctx.fillRect(0, 0, mapW, mapH);

    // Build a white "reveal" mask on a separate canvas, then erase it (blurred)
    // from the fog so every revealed shape gets soft, feathered edges.
    const mask = document.createElement('canvas');
    mask.width = mapW;
    mask.height = mapH;
    const mctx = mask.getContext('2d');
    if (!mctx) return canvas;
    mctx.fillStyle = '#fff';
    mctx.strokeStyle = '#fff';
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';

    const px = (loc: { x: number; y: number }) => ({
      x: (loc.x / 100) * mapW,
      y: (loc.y / 100) * mapH,
    });
    const exploredLocs = locations.filter((l) => explored.has(l.id));

    // 1. Fill regions fully enclosed by explored, connected locations.
    computeExploredFaces(locations, explored).forEach((poly) => {
      mctx.beginPath();
      poly.forEach((p, i) => {
        const { x, y } = px(p);
        if (i === 0) mctx.moveTo(x, y);
        else mctx.lineTo(x, y);
      });
      mctx.closePath();
      mctx.fill();
    });

    // 2. Corridors along explored-to-explored connections.
    mctx.lineWidth = CORRIDOR_W;
    exploredLocs.forEach((loc) => {
      const from = px(loc);
      (loc.connectedTo ?? []).forEach((targetId) => {
        if (!explored.has(targetId)) return;
        const target = locations.find((l) => l.id === targetId);
        if (!target) return;
        const to = px(target);
        mctx.beginPath();
        mctx.moveTo(from.x, from.y);
        mctx.lineTo(to.x, to.y);
        mctx.stroke();
      });
    });

    // 3. Solid disks around explored locations.
    exploredLocs.forEach((loc) => {
      const { x, y } = px(loc);
      mctx.beginPath();
      mctx.arc(x, y, NODE_R, 0, Math.PI * 2);
      mctx.fill();
    });

    // 4. Dim hints for connected-but-unexplored neighbours (partial reveal).
    const hintSet = new Set<string>();
    exploredLocs.forEach((loc) => {
      (loc.connectedTo ?? []).forEach((id) => {
        if (!explored.has(id)) hintSet.add(id);
      });
    });
    mctx.globalAlpha = 0.5;
    locations
      .filter((l) => hintSet.has(l.id))
      .forEach((loc) => {
        const { x, y } = px(loc);
        mctx.beginPath();
        mctx.arc(x, y, HINT_R, 0, Math.PI * 2);
        mctx.fill();
      });
    mctx.globalAlpha = 1;

    // Erase the blurred mask from the fog -> soft feathered reveals.
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.filter = `blur(${FEATHER}px)`;
    ctx.drawImage(mask, 0, 0);
    ctx.restore();

    return canvas;
  }

  function ensureFogCache(
    locations: MapLocation[],
    explored: Set<string>,
    mapW: number,
    mapH: number,
  ) {
    const key = `${locations.length}|${explored.size}|${mapW}x${mapH}|${Array.from(explored)
      .sort()
      .join(',')}`;
    if (key !== fogCacheKey.current || !fogCache.current) {
      fogCache.current = buildFogCanvas(locations, explored, mapW, mapH);
      fogCacheKey.current = key;
    }
    return fogCache.current;
  }

  function drawConnections(
    ctx: CanvasRenderingContext2D,
    selected: MapLocation,
    locations: MapLocation[],
    mapW: number,
    mapH: number,
  ) {
    const fromX = (selected.x / 100) * mapW;
    const fromY = (selected.y / 100) * mapH;
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(167, 139, 250, 0.6)';
    ctx.setLineDash([8, 8]);
    (selected.connectedTo ?? []).forEach((targetId) => {
      const target = locations.find((l) => l.id === targetId);
      if (!target) return;
      const toX = (target.x / 100) * mapW;
      const toY = (target.y / 100) * mapH;
      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(toX, toY);
      ctx.stroke();
    });
    ctx.setLineDash([]);
  }

  function drawLocations(
    ctx: CanvasRenderingContext2D,
    state: MapState,
    data: MapData,
    mapW: number,
    mapH: number,
  ) {
    const { locations, explored } = data;
    locations.forEach((loc) => {
      const x = (loc.x / 100) * mapW;
      const y = (loc.y / 100) * mapH;
      const isExplored = explored.has(loc.id);
      const isVisible = locations.some(
        (l) => explored.has(l.id) && (l.connectedTo ?? []).includes(loc.id),
      );
      const visible = isExplored || isVisible;
      if (!visible) return;

      const radius = isExplored ? 11 : 9;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = isExplored ? groupColor(loc.group) : 'rgba(255,255,255,0.35)';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = isExplored ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.6)';
      ctx.stroke();

      // Travel target ring
      if (loc.id === state.travelTarget) {
        ctx.beginPath();
        ctx.arc(x, y, radius + 6, 0, Math.PI * 2);
        ctx.strokeStyle = '#67e8f9';
        ctx.setLineDash([4, 4]);
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
      }
      // Selected ring
      if (loc.id === state.selectedId) {
        ctx.beginPath();
        ctx.arc(x, y, radius + 4, 0, Math.PI * 2);
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      if (!isExplored && isVisible) {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('?', x, y);
      }
    });
  }

  function drawPlayers(
    ctx: CanvasRenderingContext2D,
    state: MapState,
    data: MapData,
    avatars: Map<number, HTMLImageElement>,
    mapW: number,
    mapH: number,
  ) {
    data.positions.forEach((player) => {
      if (!player.current_location_id) return;
      const loc = data.locations.find((l) => l.id === player.current_location_id);
      if (!loc) return;
      const offsetSeed = player.user_id % 100;
      const angle = (offsetSeed / 100) * Math.PI * 2;
      const radius = 20;
      const cx = (loc.x / 100) * mapW + Math.cos(angle) * radius;
      const cy = (loc.y / 100) * mapH + Math.sin(angle) * radius;
      const isMe = player.user_id === state.currentPlayerId;
      const r = isMe ? 14 : 12;
      const avatar = avatars.get(player.user_id);

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      if (avatar && avatar.complete && avatar.naturalWidth > 0) {
        ctx.drawImage(avatar, cx - r, cy - r, r * 2, r * 2);
      } else {
        ctx.fillStyle = isMe ? '#a78bfa' : '#64748b';
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.font = 'bold 12px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          (player.character_name || '?').charAt(0).toUpperCase(),
          cx,
          cy,
        );
      }
      ctx.restore();

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = isMe ? '#fbbf24' : 'rgba(255,255,255,0.5)';
      ctx.stroke();
    });
  }

  function draw(args: RendererArgs) {
    const { canvas, mapImage, state, data, avatars, viewportW, viewportH } = args;
    if (!canvas || !mapImage || !mapImage.complete) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = viewportW;
    const h = viewportH;
    const mapW = mapImage.naturalWidth || 1024;
    const mapH = mapImage.naturalHeight || 1024;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#06060f';
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.scale(state.zoom, state.zoom);
    ctx.translate(state.pan.x, state.pan.y);
    ctx.translate(-mapW / 2, -mapH / 2);

    ctx.drawImage(mapImage, 0, 0, mapW, mapH);

    const fog = ensureFogCache(data.locations, data.explored, mapW, mapH);
    ctx.drawImage(fog, 0, 0);

    const selected = state.selectedId
      ? data.locations.find((l) => l.id === state.selectedId)
      : null;
    if (selected) drawConnections(ctx, selected, data.locations, mapW, mapH);

    drawLocations(ctx, state, data, mapW, mapH);
    drawPlayers(ctx, state, data, avatars, mapW, mapH);

    ctx.restore();
  }

  function scheduleDraw(args: RendererArgs) {
    // Always keep the latest args so a coalesced frame draws the freshest data.
    pendingArgsRef.current = args;
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (pendingArgsRef.current) draw(pendingArgsRef.current);
    });
  }

  function invalidateFog() {
    fogCacheKey.current = '';
    fogCache.current = null;
  }

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return { draw, scheduleDraw, invalidateFog };
}

export type { MapState, MapData, RendererArgs };
